/**
 * src/lib/api/photos.js — Project-photos client wrappers (Phase 1 data spine).
 *
 * Spec: docs/plans/2026-06-05-photos-subsystem-design.md (v2.7 Task 1).
 * Photos EXTEND project_documents (document_type='photo'); retrieval rides the
 * existing brain.documents list + the DocParser /process_image pipeline. These
 * wrappers route through apiFetch (auto-tracing) and reuse minioUpload's
 * download-url for on-demand originals.
 *
 *   registerPhoto         — POST photos.register (buffered row, idempotent on sha256)
 *   attachPhotoRag        — POST photos.attach-rag (fold /process_image result onto the row)
 *   listPhotos            — GET brain.documents?document_type=photo&... (faceted)
 *   requestPhotoOriginalUrl — presigned MinIO GET via minioUpload.requestDownloadUrl
 *   sha256Hex / makeThumbnailBlob — client capture helpers (dedup id + hot thumb)
 */

import { apiFetch, API_BASE } from './client';
import { supabase } from '../supabase';

/**
 * Resolve the vessel_id a capture belongs to from its scope. A deal and its
 * project both carry a vessel_id (the SHARED anchor that makes photos a single
 * per-vessel folder across Pipeline + Projects). Given a project_id we read
 * projects.vessel_id; given a deal_id we read crm_deals.vessel_id. Best-effort:
 * returns null on any miss so capture still works (vessel link just stays empty).
 *
 * Callers that already hold the loaded row's vessel_id should pass it directly
 * and skip this lookup — this is the fallback when only an id is in hand.
 *
 * @param {{project_id?:string, deal_id?:string, vessel_id?:string}} scope
 * @param {object} [client] — injectable supabase client (tests)
 * @returns {Promise<string|null>}
 */
export async function resolveVesselId(scope = {}, client = supabase) {
  if (scope.vessel_id) return scope.vessel_id;
  try {
    if (scope.project_id) {
      const { data } = await client
        .from('projects').select('vessel_id').eq('id', scope.project_id).maybeSingle();
      return data?.vessel_id || null;
    }
    if (scope.deal_id) {
      const { data } = await client
        .from('crm_deals').select('vessel_id').eq('id', scope.deal_id).maybeSingle();
      return data?.vessel_id || null;
    }
  } catch {
    // spine-emit-ok: best-effort vessel resolve; capture proceeds vessel-less
    return null;
  }
  return null;
}

/**
 * Register a buffered photo (after its bytes land in the photo-buffer bucket).
 * Idempotent server-side on content_sha256 — a replay returns the existing row.
 *
 * @param {object} payload
 * @param {string} [payload.project_id]
 * @param {string} [payload.deal_id]
 * @param {string} [payload.vessel_id]
 * @param {string[]} [payload.product_types]
 * @param {string} payload.content_sha256 — REQUIRED
 * @param {string} [payload.buffer_path]
 * @param {string} [payload.thumb_path]
 * @param {string} [payload.file_name]
 * @param {number} [payload.file_size]
 * @param {string} [payload.mime_type]
 * @param {number} [payload.width_px]
 * @param {number} [payload.height_px]
 * @param {object} [payload.exif]
 * @param {string} [payload.caption]
 * @returns {Promise<{success:boolean, document_id?:string, deduped?:boolean}>}
 */
export async function registerPhoto(payload) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.register', ...payload }),
  });
}

/**
 * Attach a DocParser /process_image result to a photo row (fire-and-forget after
 * register succeeds + online). The client holds the captured File so it runs
 * docparser.processImage itself and posts the raw result here.
 *
 * @param {string} documentId
 * @param {object} ragResult — the docparser.processImage return value
 * @param {object} [opts] — { context }
 * @returns {Promise<{success:boolean, document_id?:string, quality_score?:number}>}
 */
export async function attachPhotoRag(documentId, ragResult, opts = {}) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({
      action: 'photos.attach-rag',
      document_id: documentId,
      rag_result: ragResult,
      ...(opts.context ? { context: opts.context } : {}),
    }),
  });
}

/**
 * List the canonical product-type taxonomy (slug / label_en / label_es /
 * category) for the tag editor's autocomplete (Gap A). User-auth GET; role is
 * derived server-side. Pass includeProposed (ceo/admin only) to also get
 * review-queue types.
 *
 * @param {{includeProposed?:boolean}} [opts]
 * @returns {Promise<{ok:boolean, types:Array<{slug:string,label_en:string,label_es:string,category:string|null,is_canonical:boolean}>, count:number}>}
 */
export async function listProductTypes({ includeProposed = false } = {}) {
  const params = new URLSearchParams({ action: 'brain.product_types' });
  if (includeProposed) params.set('include_proposed', '1');
  return apiFetch(`${API_BASE}/ai?${params.toString()}`);
}

/**
 * Update a photo's product_types after capture (Gap A — add/edit tags). The
 * server slugifies free-typed tags (hybrid taxonomy) and re-feeds the corrected
 * set to the brain best-effort. Two modes:
 *   SET     — pass `product_types` (full replacement; [] un-tags)
 *   ADD/REM — pass `add` and/or `remove` to mutate the existing set
 *
 * @param {string} documentId — project_documents.id (document_type='photo')
 * @param {{product_types?:string[], add?:string[], remove?:string[]}} changes
 * @returns {Promise<{success:boolean, document_id?:string, product_types?:string[]}>}
 */
export async function updatePhotoTags(documentId, changes = {}) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.update_tags', document_id: documentId, ...changes }),
  });
}

/**
 * photos.update_summary — edit a photo's AI description (summary). Persists to
 * project_documents.summary so the corrected text feeds the SO doc + quote builder.
 * @param {string} documentId — project_documents.id (document_type='photo')
 * @param {string} summary
 * @returns {Promise<{success:boolean, document_id?:string, summary?:string}>}
 */
export async function updatePhotoSummary(documentId, summary) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.update_summary', document_id: documentId, summary }),
  });
}

/**
 * List a scope's photos (faceted). Reuses brain.documents with document_type=photo.
 *
 * SHARED-FOLDER (per-vessel) mode: pass `scope_union: true` together with the
 * vessel_id AND the originating project_id/deal_id. The server then matches ANY
 * of those anchors (vessel_id OR project_id OR deal_id) so a photo shot during
 * quoting (deal_id) and one shot during the project (project_id) surface in the
 * SAME gallery — the "concurrent folder between projects and pipeline". Without
 * scope_union it stays an exact-anchor (AND) query (legacy behaviour).
 *
 * @param {object} [filters]
 * @param {{project_id?:string, deal_id?:string, vessel_id?:string, bundle_id?:string}} [filters.scope]
 * @param {boolean} [filters.scope_union=false] — union the supplied anchors (per-vessel folder)
 * @param {string[]} [filters.product_types]
 * @param {number} [filters.limit=50]
 * @returns {Promise<{documents: Array}>}
 */
export async function listPhotos(filters = {}) {
  const scope = filters.scope || {};
  const params = new URLSearchParams({ action: 'brain.documents', document_type: 'photo' });
  if (scope.project_id) params.set('project_id', scope.project_id);
  if (scope.deal_id) params.set('deal_id', scope.deal_id);
  if (scope.vessel_id) params.set('vessel_id', scope.vessel_id);
  // TICKET-702 — filter to photos assigned to a specific bundle within the project.
  if (scope.bundle_id) params.set('bundle_id', scope.bundle_id);
  if (filters.scope_union) params.set('scope_union', '1');
  if (Array.isArray(filters.product_types) && filters.product_types.length) {
    params.set('product_types', filters.product_types.join(','));
  }
  // TICKET-363: free-text search over the photo description (summary).
  if (filters.q && String(filters.q).trim()) params.set('q', String(filters.q).trim());
  params.set('limit', String(filters.limit || 50));
  return apiFetch(`${API_BASE}/data?${params.toString()}`);
}

/**
 * On-demand presigned GET of a photo's original (private photo-buffer object).
 * Server loads the project_documents row, the photo read gate runs at routePhotos,
 * then it signs the buffer_path — the client never supplies a path (no IDOR).
 * NOTE: this is photos.original_url, NOT voice.download_url — a photo is a
 * project_documents row, never a voice_uploads row.
 *
 * @param {string} documentId
 * @param {object} [opts]
 * @param {boolean} [opts.download] — Content-Disposition:attachment (real save, not inline)
 * @param {boolean} [opts.thumb] — TICKET-615: caller only needs a PREVIEW-sized image
 *   (a row/tile thumbnail, not the full original) — server returns the backfilled
 *   thumb_path directly, skipping the presign round trip, when one exists. Ignored
 *   together with download:true (a download always wants the real original).
 * @returns {Promise<{download_url:string, file_name?:string, expires_in?:number}>}
 */
export async function requestPhotoOriginalUrl(documentId, { download = false, thumb = false } = {}) {
  // download:true -> the server signs the URL with Content-Disposition:attachment
  // so it SAVES (cross-origin <a download> is ignored). Inline view passes false.
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.original_url', document_id: documentId, download, thumb }),
  });
}

/**
 * Add a text comment to a photo (slice 1). user_id + created_at are stamped
 * SERVER-SIDE from the auth profile — never sent from the client. `region` is
 * RESERVED for future pinned annotations (omitted in this slice = whole-photo).
 *
 * @param {object} payload
 * @param {string} payload.photo_document_id — REQUIRED (project_documents.id)
 * @param {string} payload.body — REQUIRED comment text
 * @param {string} [payload.parent_comment_id] — threaded reply target
 * @param {object} [payload.region] — RESERVED; {x,y,w,h} normalized
 * @returns {Promise<{success:boolean, comment?:object}>}
 */
export async function addPhotoComment(payload) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.comment_add', ...payload }),
  });
}

/**
 * List a photo's non-archived comments, oldest-first, with author display name.
 *
 * @param {string} photoDocumentId — project_documents.id
 * @returns {Promise<{success:boolean, comments:Array}>}
 */
export async function listPhotoComments(photoDocumentId) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.comment_list', photo_document_id: photoDocumentId }),
  });
}

/**
 * SHA-256 hex of a Blob/File's bytes (dedup identity). Uses Web Crypto.
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export async function sha256Hex(blob) {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generate a downscaled JPEG thumbnail (longest edge `maxEdge`, default 320px)
 * for the hot photo-thumbs tier + optimistic tile. Best-effort: returns null if
 * canvas/image decode is unavailable (e.g. SSR / test without DOM).
 *
 * @param {Blob} blob — source image
 * @param {number} [maxEdge=320]
 * @returns {Promise<Blob|null>}
 */
export async function makeThumbnailBlob(blob, maxEdge = 320) {
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') return null;
  let bitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null; // undecodable image → caller falls back to the original
  }
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.8);
  });
}

/**
 * photos.set_pinned — pin/unpin a photo so it sorts first in the pickers.
 * CEO 2026-07-26: "lets add a favourite or a pin so its easier to find here."
 * Stored in project_documents.document_metadata.pinned (merged server-side, so the
 * storage pointers written at capture survive) — no migration.
 * @param {string} documentId — project_documents.id (document_type='photo')
 * @param {boolean} pinned
 */
export async function setPhotoPinned(documentId, pinned) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.set_pinned', document_id: documentId, pinned: !!pinned }),
  });
}

/**
 * photos.set_class — TICKET-580. Mark a photo as this job's own ('project') or as
 * reference material that merely lives on this job ('reference').
 *
 * CEO 2026-07-30: project/customer images are one section of the Files folder and
 * feed the quote's project-photo annex; reference images are a separate section.
 * Filing a portfolio picture on a deal used to make it indistinguishable from the
 * boat's own photos on every surface.
 *
 * Stored in project_documents.document_metadata.photo_class (merged server-side,
 * beside `pinned`) — no migration. Absent = 'project'.
 * @param {string} documentId — project_documents.id (document_type='photo')
 * @param {'project'|'reference'} photoClass
 */
export async function setPhotoClass(documentId, photoClass) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.set_class', document_id: documentId, photo_class: photoClass }),
  });
}

/**
 * photos.assign_scope — TICKET-506. Attribute a SELECTION of reference photos to a
 * boat in one call.
 *
 * CEO 2026-07-28: "I could identify them also from which project are they... so when
 * we go to a directory, let's say, from Guillermo Londner, I find this images."
 * Photos ingested from a flat folder carry no vessel/project/deal, and `brain.documents`
 * refuses a query with no anchor — so an unattributed photo is unreachable from the
 * Directory, not merely hard to find.
 *
 * Pass a project or a deal and the server derives the vessel (resolveVesselId,
 * TICKET-459) — the caller never has to know the boat. `scope_level` stays 'company':
 * a reference photo remains reusable everywhere AND gains its link.
 *
 * @param {string[]} documentIds — project_documents.id list (max 200 per call)
 * @param {{project_id?: string, deal_id?: string, vessel_id?: string}} scope
 * @returns {Promise<{success: boolean, updated: number, vessel_id: string|null, skipped: string[]}>}
 *   `skipped` names every id that was NOT updated (not a photo, missing, archived) —
 *   check it, because a bare count reads as full success on a partial write.
 */
export async function assignPhotoScope(documentIds, scope = {}) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.assign_scope', document_ids: documentIds, ...scope }),
  });
}

/**
 * photos.archive — TICKET-669. Soft-archive a SELECTION of photos (bulk).
 * Never a hard delete; archived rows stop appearing in the gallery/quote
 * picker but remain in the database, recoverable.
 *
 * @param {string[]} documentIds — project_documents.id list (max 200 per call)
 * @returns {Promise<{success: boolean, archived: number, skipped: string[]}>}
 *   `skipped` names every id that was NOT archived (not a photo, missing,
 *   already archived) — check it, same partial-write reason as assign_scope.
 */
export async function archivePhotos(documentIds) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.archive', document_ids: documentIds }),
  });
}

/**
 * TICKET-702 — assign photos to a bundle within their project. Thin wrapper over
 * assignPhotoScope (photos.assign_scope already accepts bundle_id server-side, no
 * role gate — any authenticated worker can add). Kept as a named export so callers
 * (BundlePhotoPickerSheet) read intent, not the generic scope-assign shape.
 *
 * @param {string[]} documentIds
 * @param {{project_id: string, bundle_id: string}} scope
 * @returns {Promise<{success: boolean, updated: number, skipped: string[]}>}
 */
export async function assignPhotoToBundle(documentIds, scope) {
  return assignPhotoScope(documentIds, scope);
}

/**
 * photos.remove-from-bundle — TICKET-702. Clears bundle_id on the given photos
 * (never archives them). Server-side role-gated to ceo/pmo/admin only.
 *
 * @param {string[]} documentIds — project_documents.id list (max 200 per call)
 * @returns {Promise<{success: boolean, cleared: number, skipped: string[]}>}
 */
export async function removePhotoFromBundle(documentIds) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'photos.remove-from-bundle', document_ids: documentIds }),
  });
}
